// src/js/main.js - entry for bundling
import installCartBadge from './helpers/cart-badge.js';
import installNotify from './helpers/notify.js';
import { debounce } from './helpers/utils.js';
import plancAddToCart from './helpers/planc-add-to-cart.js';

// feature modules (safe, idempotent initializers)
// Cart will be lazy-loaded on pages that include cart UI to reduce initial bundle size.
let _initCart = null;
// Checkout will be lazy-loaded to keep the initial bundle smaller.
// We expose a loader on `window.initCheckout` below so templates can call it.
// We'll lazy-load the dynamic-products module to reduce initial bundle size.
// The module will be imported only when a `.planc-dynamic-products-section` exists.
let initDynamicProducts = null;
import { initNavigation } from './modules/navigation.js';
import { initHeaderMenu } from './modules/header-menu.js';
import { initWishlist } from './modules/wishlist.js';

// ensure debug flag exists
if (typeof window.PLANC_DEBUG === 'undefined') window.PLANC_DEBUG = false;

// install helpers (no-op if already present)
installCartBadge();
installNotify();
window.PlancUtils = window.PlancUtils || {};
if (!window.PlancUtils.debounce) window.PlancUtils.debounce = debounce;

// Expose idempotent initializers on `window` for legacy templates that call
// `initEnhancedCart`, `initCheckout`, etc. This is safe because module
// initializers are written to be idempotent.
try {
	// Provide a dynamic loader for cart so it can be code-split.
	if (!window.initCart) {
		window.initCart = function () {
			return import('./modules/cart.js').then(mod => {
				try {
					const fn = mod.initCart || mod.default || mod.init;
					if (typeof fn === 'function') fn();
				} catch (e) {
					if (window.PLANC_DEBUG && console) console.debug('planc: cart init failed', e);
				}
			}).catch(e => { if (window.PLANC_DEBUG && console) console.debug('planc: cart import failed', e); });
		};
	}
	window.initEnhancedCart = window.initEnhancedCart || window.initCart;
	// Provide a dynamic loader for checkout so it can be code-split.
	if (!window.initCheckout) {
		window.initCheckout = function () {
			return import('./modules/checkout.js').then(mod => {
				try {
					const fn = mod.initCheckout || mod.default || mod.init;
					if (typeof fn === 'function') fn();
				} catch (e) {
					if (window.PLANC_DEBUG && console) console.debug('planc: checkout init failed', e);
				}
			}).catch(e => { if (window.PLANC_DEBUG && console) console.debug('planc: checkout import failed', e); });
		};
	}
	window.initDynamicProducts = window.initDynamicProducts || initDynamicProducts;
	window.initNavigation = window.initNavigation || initNavigation;
	window.initHeaderMenu = window.initHeaderMenu || initHeaderMenu;
	window.initWishlist = window.initWishlist || initWishlist;
} catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: expose inits failed', e); }

// Backwards-compatibility shims: populate legacy global localization objects
// from the consolidated `planc` payload so older per-script code continues
// to work when the site is running the single bundled `main.js`.
try {
	if (typeof window !== 'undefined' && window.planc && typeof window.planc === 'object') {
		// dynamic products legacy object
		if (!window.planc_products_ajax && window.planc.products) {
			window.planc_products_ajax = window.planc_products_ajax || {};
			window.planc_products_ajax.ajax_url = window.planc.products.ajax_url || window.planc.ajaxUrl || (window.location.origin + '/wp-admin/admin-ajax.php');
			window.planc_products_ajax.nonce = window.planc.products.nonce || '';
			window.planc_products_ajax.error_text = window.planc.products.error_text || '';
		}

		// cart legacy params
		if (!window.plan_c_cart_params && window.planc.cart) {
			window.plan_c_cart_params = window.plan_c_cart_params || {};
			window.plan_c_cart_params.ajax_url = window.planc.ajaxUrl || window.planc.cart.ajax_url || (window.location.origin + '/wp-admin/admin-ajax.php');
			window.plan_c_cart_params.cart_nonce = window.planc.cart.cart_nonce || '';
		}

		// subscribe legacy params
		if (!window.plancSubscribe && window.planc.subscribe) {
			window.plancSubscribe = window.plancSubscribe || {};
			window.plancSubscribe.ajax_url = window.planc.subscribe.ajax_url || window.planc.ajaxUrl || (window.location.origin + '/wp-admin/admin-ajax.php');
			window.plancSubscribe.nonce = window.planc.subscribe.nonce || '';
		}

		// navigation legacy object
		if (!window.plancNav && window.planc.nav) {
			window.plancNav = window.planc.nav;
		}
	}
} catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: legacy shims failed', e); }

// Initialize safe feature modules when DOM is ready. These are written to be idempotent
// so they won't double-bind if the original asset files are still enqueued.
(function () {
	// Global delegated add-to-cart handler (capture phase)
	// Ensures one authoritative handler runs before other bubble-phase handlers
	try {
		const ADD_SELECTORS = ['.add-to-cart-ajax', '.add-to-cart-btn', '.single_add_to_cart_button', '.product .button', 'button[name="add-to-cart"]'];
		function findAddToCartButton(target) {
			if (!target) return null;
			for (let sel of ADD_SELECTORS) {
				const el = target.closest ? target.closest(sel) : null;
				if (el) return el;
			}
			return null;
		}

		document.addEventListener('click', function (e) {
			try {
				if (e.defaultPrevented) return;
				if (e.button && e.button !== 0) return;
				if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

				const btn = findAddToCartButton(e.target);
				if (!btn) return;

				e.preventDefault();
				e.stopImmediatePropagation && e.stopImmediatePropagation();
				e.stopPropagation && e.stopPropagation();

				const $ = window.jQuery || window.$;
				const $btn = $ ? $(btn) : null;

				let productId = null;
				try {
					if ($btn && $btn.length) productId = $btn.data('product-id') || $btn.attr('data-product-id') || $btn.data('id') || $btn.attr('data-id') || null;
					if (!productId && btn.hasAttribute) {
						productId = btn.getAttribute('data-product-id') || btn.getAttribute('data-id') || btn.getAttribute('value') || null;
					}
					if (!productId && btn.tagName && btn.tagName.toLowerCase() === 'a') {
						const href = btn.getAttribute('href') || '';
						const m = href.match(/[?&]add-to-cart=(\d+)/);
						if (m) productId = m[1];
					}
				} catch (err) { if (window.PLANC_DEBUG && console) console.debug('planc global id extraction failed', err); }

				if (!productId) {
					try {
						const anc = btn.closest && btn.closest('[data-product-id], [data-id], [data-product], [data-product_id]');
						if (anc) productId = anc.getAttribute('data-product-id') || anc.getAttribute('data-id') || anc.getAttribute('data-product') || anc.getAttribute('data-product_id') || null;
					} catch (err) { /* ignore */ }
				}

				if (!productId) {
					if (window.PLANC_DEBUG && console) console.debug('planc global: product id not found for add-to-cart click', btn);
					return;
				}

				try {
					if (typeof plancAddToCart === 'function') {
						plancAddToCart({ productId: productId, quantity: 1, $button: $btn || null, optimistic: true }).catch((err) => {
							if (window.PLANC_DEBUG && console) console.debug('plancAddToCart failed', err);
						});
						return;
					}
				} catch (err) { if (window.PLANC_DEBUG && console) console.debug('planc global handler call failed', err); }
			} catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc global add-to-cart handler error', e); }
		}, true);
	} catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc global add-to-cart init failed', e); }
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => {
			// initialize cart via dynamic loader (idempotent)
			try { window.initCart && window.initCart(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: initCart call failed', e); }
			// initialize checkout via dynamic loader (idempotent)
			try { window.initCheckout && window.initCheckout(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: initCheckout call failed', e); }
			// lazy-load dynamic products only when needed
			try {
				if (document.querySelector('.planc-dynamic-products-section')) {
					import('./modules/dynamic-products.js').then(mod => {
						try { (mod.initDynamicProducts || mod.default || mod.init)(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: dynamic-products init failed', e); }
					}).catch(e => { if (window.PLANC_DEBUG && console) console.debug('planc: dynamic-products import failed', e); });
				}
			} catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: dynamic-products import check failed', e); }
			// Navigation (search overlay, user menu toggles)
			try { initNavigation(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: navigation init failed', e); }
			try { initHeaderMenu(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: header-menu init failed', e); }
			try { initWishlist(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: wishlist init failed', e); }
		});
	} else {
		// initialize cart via dynamic loader (idempotent)
		try { window.initCart && window.initCart(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: initCart call failed', e); }
		// initialize checkout via dynamic loader (idempotent)
		try { window.initCheckout && window.initCheckout(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: initCheckout call failed', e); }
			try {
				if (document.querySelector('.planc-dynamic-products-section')) {
					import('./modules/dynamic-products.js').then(mod => {
						try { (mod.initDynamicProducts || mod.default || mod.init)(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: dynamic-products init failed', e); }
					}).catch(e => { if (window.PLANC_DEBUG && console) console.debug('planc: dynamic-products import failed', e); });
				}
			} catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: dynamic-products import check failed', e); }
			try { initNavigation(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: navigation init failed', e); }
		try { initHeaderMenu(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: header-menu init failed', e); }
			try { initWishlist(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: wishlist init failed', e); }
	}
})();
// Export nothing; this file bundles helpers into final assets/js/main.js
